// ─────────────────────────────────────────────────────────────────────────────
// RELAX (Relay) MODE — centralized route + theme configuration
//
// Relax is the light-theme counterpart to Rush (Sprint). It reuses the same
// component architecture, layouts and business logic as Rush; only the route
// namespace, the data source (brands instead of restaurants) and the visual
// theme differ.
//
// The canonical route namespace is `/relax`. The legacy `/phase2` namespace is
// preserved via redirects (see src/index.js) so existing links/bookmarks keep
// working.
// ─────────────────────────────────────────────────────────────────────────────

export const RELAX_BASE = "/relax";
export const LEGACY_BASE = "/phase2";

// Canonical Relax routes. Use these everywhere instead of hardcoding "/phase2".
export const RelaxRoutes = {
  home: `${RELAX_BASE}`,
  categories: `${RELAX_BASE}/categories`,
  category: (slug) => `${RELAX_BASE}/category/${slug}`,
  products: (categorySlug, subcategorySlug) =>
    `${RELAX_BASE}/products/${categorySlug}/${subcategorySlug}`,
  product: (slug) => `${RELAX_BASE}/product/${slug}`,
  browse: (tag) => `${RELAX_BASE}/browse/${tag}`,
  explore: `${RELAX_BASE}/explore`,
  myAccount: `${RELAX_BASE}/my-account`,
  // Relax-specific: brand listing + brand items page
  brands: `${RELAX_BASE}/brands`,
  brand: (id) => `${RELAX_BASE}/brand/${id}`,
};

// True when the given pathname belongs to the Relax experience.
// Accepts both the canonical `/relax` and the legacy `/phase2` namespaces so
// callers don't have to special-case redirects.
export const isRelaxPath = (pathname = "") =>
  pathname.startsWith(RELAX_BASE) || pathname.startsWith(LEGACY_BASE);

// Map a legacy `/phase2*` pathname onto the canonical `/relax*` namespace.
export const toRelaxPath = (pathname = "") =>
  pathname.startsWith(LEGACY_BASE)
    ? RELAX_BASE + pathname.slice(LEGACY_BASE.length)
    : pathname;

// ─────────────────────────────────────────────────────────────────────────────
// Light-theme design tokens (premium minimal, iOS-like). Derived from the
// Relax Figma. Shared so pages don't hardcode colors.
// ─────────────────────────────────────────────────────────────────────────────
export const RelaxTheme = {
  font: "'Montserrat', sans-serif",
  // Surfaces
  pageBg: "#ffffff",
  surface: "#ffffff",
  headerBg: "#ffffff",
  // Card info bar (kept dark per Figma for contrast on store/brand cards)
  cardInfoBg: "#271a46",
  cardInfoText: "#ffffff",
  cardImageBg: "#d9d9d9",
  // Text
  textPrimary: "#111111",
  textSecondary: "#a9a2bf",
  textMuted: "#919191",
  // Inputs / search (soft gray, thin border)
  inputBg: "#f4f4f6",
  inputBorder: "rgba(187,126,255,0.55)",
  inputPlaceholder: "#a9a2bf",
  // Accents
  accentPink: "#e2479b",
  accentGreen: "#00c026",
  star: "#ffcf00",
  // Borders / shadows
  hairline: "#f0ebff",
  softShadow: "0 4px 14px rgba(17,17,17,0.08)",
  // Toggle (Rush/Relax)
  toggleTrackBg: "#271b47",
  toggleTrackBorder: "#bb7eff",
  toggleActiveBg: "#b0055a",
  toggleActiveBorder: "#ee499b",
  radius: { sm: 8, md: 9, pill: 129 },
};
